import { ListItem, makeStyles } from '@rneui/themed'
import { timeStringFrom24HourTimeString } from '@nextforce/api'
import { t } from 'app/i18l'
import React, { useState } from 'react'
import {
  BooleanSetting,
  DollarsSetting,
  MultiSelectSetting,
  NumberSetting,
  OnBooleanSettingChanged,
  Setting,
  SingleSelectSetting,
  TextSetting,
  TimeRangeSetting,
} from 'settings/types'
import { BooleanRow } from 'shared/components/boolean_row'

interface Props {
  setting: Setting
  onPress(setting: Setting): void
  onBooleanSettingChanged: OnBooleanSettingChanged
}

interface RowProps<T = Setting> {
  setting: T
  onPress(): void
  onBooleanSettingChanged?: OnBooleanSettingChanged
}

export function SettingRow({
  setting,
  onPress,
  onBooleanSettingChanged,
}: Props) {
  const onRowPress = () => onPress(setting)

  switch (setting.type) {
    case 'boolean':
      return (
        <BooleanSettingRow
          setting={setting}
          onBooleanSettingChanged={onBooleanSettingChanged}
          onPress={onRowPress}
        />
      )
    case 'text':
      return <TextRow setting={setting} onPress={onRowPress} />
    case 'dollars':
      return <DollarsRow setting={setting} onPress={onRowPress} />
    case 'number':
      return <NumberRow setting={setting} onPress={onRowPress} />
    case 'timeRange':
      return <TimeRangeRow setting={setting} onPress={onRowPress} />
    case 'singleSelect':
      return <SingleSelectRow setting={setting} onPress={onRowPress} />
    case 'multiSelect':
      return <MultiSelectRow setting={setting} onPress={onRowPress} />
  }
}

function BooleanSettingRow({
  setting,
  onBooleanSettingChanged,
}: RowProps<BooleanSetting>) {
  const [value, setValue] = useState(setting.on)

  async function onValueChange() {
    const newValue = !value
    setValue(newValue)
    onBooleanSettingChanged &&
      (await onBooleanSettingChanged(setting, newValue))
  }
  return (
    <BooleanRow title={setting.name} value={value} onToggle={onValueChange} />
  )
}

function TextRow({ setting, onPress }: RowProps<TextSetting>) {
  const styles = useStyles()
  return (
    <ListItem onPress={onPress}>
      <ListItem.Content>
        <ListItem.Title>{setting.name}</ListItem.Title>
      </ListItem.Content>
      <ListItem.Content right>
        <ListItem.Subtitle numberOfLines={1} style={styles.selectedText}>
          {setting.text}
        </ListItem.Subtitle>
      </ListItem.Content>
      <ListItem.Chevron />
    </ListItem>
  )
}

function DollarsRow({ setting, onPress }: RowProps<DollarsSetting>) {
  const styles = useStyles()
  return (
    <ListItem onPress={onPress}>
      <ListItem.Content>
        <ListItem.Title>{setting.name}</ListItem.Title>
      </ListItem.Content>
      <ListItem.Content right>
        <ListItem.Subtitle numberOfLines={1} style={styles.selectedText}>
          {setting.text}
        </ListItem.Subtitle>
      </ListItem.Content>
      <ListItem.Chevron />
    </ListItem>
  )
}

function NumberRow({ setting, onPress }: RowProps<NumberSetting>) {
  const styles = useStyles()
  return (
    <ListItem onPress={onPress}>
      <ListItem.Content>
        <ListItem.Title>{setting.name}</ListItem.Title>
      </ListItem.Content>
      <ListItem.Content right>
        <ListItem.Subtitle numberOfLines={1} style={styles.selectedText}>
          {setting.number}
        </ListItem.Subtitle>
      </ListItem.Content>
      <ListItem.Chevron />
    </ListItem>
  )
}

function TimeRangeRow({ setting, onPress }: RowProps<TimeRangeSetting>) {
  const styles = useStyles()
  return (
    <ListItem onPress={onPress}>
      <ListItem.Content>
        <ListItem.Title>{setting.name}</ListItem.Title>
      </ListItem.Content>
      <ListItem.Content right>
        <ListItem.Subtitle
          style={styles.selectedText}
        >{`${timeStringFrom24HourTimeString(
          setting.startTime
        )}\n${timeStringFrom24HourTimeString(
          setting.endTime
        )}`}</ListItem.Subtitle>
      </ListItem.Content>
      <ListItem.Chevron />
    </ListItem>
  )
}

function SingleSelectRow({ setting, onPress }: RowProps<SingleSelectSetting>) {
  const styles = useStyles()
  return (
    <ListItem onPress={onPress}>
      <ListItem.Content>
        <ListItem.Title>{setting.name}</ListItem.Title>
      </ListItem.Content>
      <ListItem.Content right>
        <ListItem.Subtitle style={styles.selectedText}>
          {selectedName(setting)}
        </ListItem.Subtitle>
      </ListItem.Content>
      <ListItem.Chevron />
    </ListItem>
  )
}

function MultiSelectRow({ setting, onPress }: RowProps<MultiSelectSetting>) {
  const styles = useStyles()
  return (
    <ListItem onPress={onPress}>
      <ListItem.Content>
        <ListItem.Title>{setting.name}</ListItem.Title>
      </ListItem.Content>
      <ListItem.Content right>
        <ListItem.Subtitle style={styles.selectedText}>
          {numberOfSelectedValues(setting)}
        </ListItem.Subtitle>
      </ListItem.Content>
      <ListItem.Chevron />
    </ListItem>
  )
}

function numberOfSelectedValues(setting: MultiSelectSetting): string {
  const count = setting.selectedIds.length
  return count ? `${count} ${t('selected')}` : t('none')
}

function selectedName(setting: SingleSelectSetting): string | undefined {
  const value = setting.values.find(v => v.id === setting.selectedId)
  return value?.name
}

const useStyles = makeStyles(theme => ({
  selectedText: {
    color: theme.colors?.grey1,
  },
}))
